import * as React from 'react';

import { compose } from 'recompose';

import { useLocation } from 'js/lib/useRouter';
import user, { isAuthenticatedUser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { allContractsHardPausedOrTerminated } from 'bundles/enterprise-admin-contracts/enterprise-admin-contracts-index';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import ProgramArchivedDenyModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramArchivedDenyModal';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import { BROWSE_CATEGORY_SLUGS } from 'bundles/unified-common/constants/enterprise';
import { ORG_SCOPED_HOME_OVERRIDE_ORG_IDS } from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useEnterpriseContracts,
  useIsProgramLoading,
  useProgram,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useIsJitOrganization } from 'bundles/unified-common/hooks/useIsJitOrganization';
import { useIsOrgScopedHome } from 'bundles/unified-common/hooks/useIsOrgScopedHome';
import { useProgramAutoJoinDomainAllowList } from 'bundles/unified-common/hooks/useProgramAutoJoinDomainAllowList';
import { useSsoLockedOutStatus } from 'bundles/unified-common/hooks/useSsoLockedOutStatus';
import { getMyLearningRedirectUrl } from 'bundles/unified-common/utils/getMyLearningRedirectUrl';
import { normalizeSearchString } from 'bundles/unified-common/utils/orgSearchQuery';
import { isWithinSsoGracePeriod } from 'bundles/unified-common/utils/ssoGracePeriod';

type Props = {
  naptime?: InjectedNaptime;
  children: React.ReactNode;
};

export const EnterprisePageGuard = ({ children, naptime }: Props): React.ReactNode | null => {
  const isOrgScopedHomeEnabled = useIsOrgScopedHome();
  const program = useProgram();
  const enterpriseContracts = useEnterpriseContracts();
  const isProgramLoading = useIsProgramLoading();
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const isFeaturesLoading = useEnterpriseProductFeaturesLoading();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { isSsoLockedOut: isSsoLockedOutAdminSetting, isLoading: isSsoLockedAdminSettingLoading } =
    useSsoLockedOutStatus(thirdPartyOrg?.id ?? '');

  const isAttemptSSOLogin = location.query.attemptSSOLogin === 'true';
  const isUserAuthenticated = isAuthenticatedUser();
  const { isJit, isLoading: isJitLoading } = useIsJitOrganization({
    orgId: thirdPartyOrg?.id,
    skip: !isAttemptSSOLogin || !isUserAuthenticated,
  });

  const { enableProgramAutoJoin, autoJoinSettled, isMembershipLoading, membership } =
    useProgramAutoJoinDomainAllowList();

  // Wait for all required data to load before making decisions
  if (isSsoLockedAdminSettingLoading || (isAttemptSSOLogin && isUserAuthenticated && isJitLoading)) {
    return;
  }
  // For LFO deeplink flows, bypass the Statsig gate and use the override org list or product
  // feature directly. The __204u cookie (stable ID) changes during SAML login, causing the gate
  // to fail for new users mid-flow (HOME-5233).
  const isLfoDeeplink = location.query.isExternal === 'true' || !!location.query.deepLinkItemId;
  const isOverrideOrg = !!thirdPartyOrg?.id && ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(thirdPartyOrg.id);
  const isFeatureEnabledOrg = !!features?.enableOrgScopedHome;
  const shouldRedirectToOrgScoped = isOrgScopedHomeEnabled || (isLfoDeeplink && (isOverrideOrg || isFeatureEnabledOrg));

  // Independent of the org-scoped-home rollout above: programs opted into
  // enableProgramRedirectToMyLearning (e.g. Pearson's LTI-linked programs) always redirect
  // straight to org-scoped My Learning, regardless of shouldRedirectToOrgScoped. (LRNRSCCSS-6546)
  const myLearningRedirectUrl = getMyLearningRedirectUrl({
    enableProgramRedirectToMyLearning: !!features?.enableProgramRedirectToMyLearning,
    programSlug: program?.slug,
    orgSlug: thirdPartyOrg?.slug,
    pathname: location.pathname,
  });
  const shouldRedirect = shouldRedirectToOrgScoped || !!myLearningRedirectUrl;

  // The product-feature query that gates `isOrgScopedHomeEnabled` resolves in a later
  // loadDataFromTree pass than the one in which this page becomes renderable. Without this
  // hold, pass 1 sees `features === undefined`, renders children, and fires every provider
  // query below this guard — all of which is discarded when the redirect throws on a later
  // pass. Scoped narrowly to program routes that could redirect, so no other enterprise page
  // changes behaviour. (LRNRSCCSS-6315)
  if (
    !shouldRedirect &&
    isFeaturesLoading &&
    !isProgramLoading &&
    program &&
    thirdPartyOrg?.slug &&
    location.pathname.startsWith('/programs/')
  ) {
    return null;
  }

  if (
    shouldRedirect &&
    !isProgramLoading &&
    program &&
    thirdPartyOrg?.slug &&
    location.pathname.startsWith('/programs/')
  ) {
    const programSlug = program.slug;
    const programPrefix = `/programs/${programSlug}`;
    if (location.pathname === programPrefix || location.pathname.startsWith(programPrefix + '/')) {
      // Domain-allowlist: hold the redirect until the auto-join (fired by the effect above) settles. Also
      // hold while membership is still loading, so we don't bounce a (possibly allowlisted) learner before
      // the join can run. Non-allowlist learners fall through and redirect now. We redirect on settle
      // regardless of join success or failure (see the effect comment above).
      if (enableProgramAutoJoin && !autoJoinSettled && (isMembershipLoading || membership?.isAllowlisted)) {
        return null;
      }

      if (myLearningRedirectUrl) {
        redirectClientOrServer(myLearningRedirectUrl);
        return null;
      }

      const subpath = location.pathname.slice(programPrefix.length) || '';
      // /browse maps to /search; domain category routes (/business, /computer-science, etc.)
      // map to org-scoped browse routes (/browse/:domainSlug).
      const subpathBase = subpath.split('/')[1] || '';
      const isBrowseRoot = subpathBase === 'browse';
      const isBrowseCategoryRoute = BROWSE_CATEGORY_SLUGS.has(subpathBase);
      let orgPath: string;
      if (isBrowseRoot) {
        orgPath = `/organizations/${thirdPartyOrg.slug}/search`;
      } else if (isBrowseCategoryRoute) {
        orgPath = `/organizations/${thirdPartyOrg.slug}/browse/${subpathBase}`;
      } else {
        orgPath = `/organizations/${thirdPartyOrg.slug}${subpath}`;
      }
      let searchPart = normalizeSearchString(location.search || '');
      // Append programId for deep link redirects so the org-scoped XDP knows which program
      // to use for enrollment (read by EnterpriseProgramAndMembershipDataProvider).
      // isExternal indicates an LFO (Learner Flow Optimization) deeplink from an external
      // platform (LMS/LXP) — the org-scoped XDP needs programSlug to resolve programId
      // and trigger auto-enrollment.
      // Also preserve it for a domain-allowlisted learner: org-home's own membership query
      // never surfaces allowlist-only eligibility (no invitation/roster row backs it), so the
      // org-scoped email-verification check needs the specific programId to look up instead.
      const hasDeepLinkParams =
        location.query.showMiniModal ||
        location.query.deepLinkItemId ||
        location.query.productId ||
        location.query.isExternal;
      if ((hasDeepLinkParams || membership?.isAllowlisted) && program?.id) {
        const params = new URLSearchParams(searchPart ? searchPart.replace(/^\?/, '') : '');
        params.set('programId', program.id);
        params.set('programSlug', program.slug);
        searchPart = '?' + params.toString().replace(/\+/g, '%20');
      }
      redirectClientOrServer(orgPath + searchPart);

      // the guard from continuing to evaluate SSO/auth logic on the old program-scoped URL
      return null;
    }
  }

  const isLearnerPartOfLfoEnabledOrg = location.query.isExternal === 'true';
  const isOrgWhiteListedForSSOLogin =
    features?.whitelistOrganizationForSSOLogin || features?.whitelistOrganizationProgramsForSSOLogin;
  const isSsoLockedOutEnterprise = features?.enableUnifiedSso
    ? isSsoLockedOutAdminSetting
    : features?.enableForceSsoLogin || features?.enableForceSsoLoginV2Beta;
  const shouldRedirectToIdpOnOrg =
    isLearnerPartOfLfoEnabledOrg && isSsoLockedOutEnterprise && isOrgWhiteListedForSSOLogin;

  /**
   * When attempting SSO login, always redirect if user is not authenticated,
   * or if it's a JIT organization and the user is not within the SSO grace period
   */
  const shouldRedirectForAttemptSSO =
    isAttemptSSOLogin && (!isUserAuthenticated || (isJit && !isWithinSsoGracePeriod(thirdPartyOrg?.id ?? '')));
  const shouldRedirectToIdp = shouldRedirectForAttemptSSO || (!isUserAuthenticated && shouldRedirectToIdpOnOrg);

  if (shouldRedirectToIdp && thirdPartyOrg && thirdPartyOrg.loginMethod === 'SAML') {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrg.id} />;
  }

  const shouldRedirectToLoginForLfo =
    !isAuthenticatedUser() &&
    isFeaturesLoading === false &&
    location.query.isExternal &&
    !location.query.attemptSSOLogin;

  if (shouldRedirectToLoginForLfo) {
    redirectClientOrServer('/login?redirectTo=' + encodeURIComponent(location.pathname + location.search));
    return null;
  }

  // Show program archived page if program is archived.
  if (
    !isProgramLoading &&
    program &&
    (program?.endedAt || (enterpriseContracts && allContractsHardPausedOrTerminated(enterpriseContracts)))
  ) {
    return <ProgramArchivedDenyModal userId={user.get()?.id} naptime={naptime} />;
  }

  // If no program found, render NotFound page.
  if (!isProgramLoading && !program) {
    if (inServerContext) {
      throw new NotFoundError();
    } else {
      return <NotFoundPage />;
    }
  }

  return <React.Fragment>{children}</React.Fragment>;
};

/**
 * @description Checks data required to serve an enterprise page, if said data is not present returns appropriate error page.
 */
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterprisePageGuard);
